import { request } from './client'

/* **W-129, SECOND CARD — THE HOTEL'S OWN LIST OF REQUESTS.** Sprint 56.

   Six kinds of guest request have been in this product since Sprint 1 because a developer
   wrote them into a seed file. They are a guess at what a hotel in Najaf is asked for, and
   the week after go-live somebody will want "prayer mat" on the list, or will want turndown
   off for Ramadan. Until this file the only way to get either was a release.

   The rules the screen has to honour are the SERVER's and are asserted there; what matters
   on this side is that the screen never offers a control the server would refuse. Hence
   `requestsEverRaised` on every row — it is what decides whether a delete button is drawn
   at all, and a button that is visible and then says no has already wasted a decision. */

export interface RequestCategoryRow {
  categoryId: string
  /** `EXTRA_TOWELS`. **Typed once and never again** — it is what every report counts by. */
  code: string
  name: string
  nameAr: string | null
  /** Minutes from being raised to being promised. The hotel's commitment, in data. */
  promiseMinutes: number
  /** Whether "how many?" is a sensible question. Towels yes, turndown no. */
  isCountable: boolean
  stockItemId: string | null
  /** Resolved, so the row says "Bath towel" rather than a GUID. Null for most. */
  stockItemName: string | null
  stockItemNameAr: string | null
  sortOrder: number
  isActive: boolean
  /** How many have EVER been raised against it. Decides whether it may be deleted. */
  requestsEverRaised: number
  openRequests: number
}

/**
 * **THE ADMIN LIST INCLUDES THE RETIRED ROWS**, and the picker's list does not. Two lists
 * answering two questions: a retired row that vanished from here could never be switched
 * back on, and a retired row still offered on the front desk is the failure this screen was
 * built to make impossible.
 */
export const fetchRequestCategories = (
  propertyId: string,
  options: { signal?: AbortSignal } = {},
) => request<RequestCategoryRow[]>(
  `/properties/${propertyId}/request-categories/admin`,
  options.signal ? { signal: options.signal } : {},
)

export const addRequestCategory = (
  propertyId: string,
  body: {
    code: string
    name: string
    nameAr: string
    promiseMinutes: number
    isCountable: boolean
    stockItemId: string | null
  },
) => request<RequestCategoryRow[]>(`/properties/${propertyId}/request-categories`, {
  method: 'POST',
  body,
})

/** **NO CODE.** There is nowhere to put one, which is what makes a rename impossible. */
export const saveRequestCategory = (
  propertyId: string,
  categoryId: string,
  body: {
    name: string
    nameAr: string
    promiseMinutes: number
    isCountable: boolean
    stockItemId: string | null
  },
) => request<RequestCategoryRow[]>(
  `/properties/${propertyId}/request-categories/${categoryId}`,
  { method: 'PUT', body },
)

/** On or off. One call, because they are one decision seen from two sides. */
export const setRequestCategoryActive = (
  propertyId: string,
  categoryId: string,
  isActive: boolean,
) => request<RequestCategoryRow[]>(
  `/properties/${propertyId}/request-categories/${categoryId}/active`,
  { method: 'PUT', body: { isActive } },
)

/**
 * **THE ONLY DELETE IN THIS PRODUCT'S API**, and it works only while nothing was ever
 * raised against the row. Everything else in this model retires, closes or cancels, because
 * everything else is part of what happened in the hotel.
 */
export const deleteRequestCategory = (
  propertyId: string,
  categoryId: string,
) => request<RequestCategoryRow[]>(
  `/properties/${propertyId}/request-categories/${categoryId}`,
  { method: 'DELETE' },
)

/**
 * **WHAT SOMEBODY TYPED, AS A CODE** — `extra towels` → `EXTRA_TOWELS`.
 *
 * A copy of the server's `RequestCategoryRules.SuggestCode`, and the duplication is
 * deliberate: this runs as somebody types, and a round trip per keystroke to be told what
 * the box would become is a screen that stutters. The server is still the one that decides;
 * this only ever fills in a suggestion the person can see and overwrite.
 *
 * Returns an empty string when nothing sensible can be made — an Arabic name has no ASCII
 * code hiding in it, and inventing one would be worse than leaving the box empty.
 */
export const suggestCategoryCode = (typed: string): string => {
  const chars: string[] = []

  for (const c of typed.trim().toUpperCase()) {
    if (/[A-Z0-9]/.test(c)) chars.push(c)
    else if (chars.length > 0 && chars[chars.length - 1] !== '_') chars.push('_')
  }

  while (chars.length > 0 && chars[chars.length - 1] === '_') chars.pop()
  while (chars.length > 0 && !/[A-Z]/.test(chars[0]!)) chars.shift()

  let code = chars.join('')
  if (code.length > 32) code = code.slice(0, 32).replace(/_+$/, '')

  return codeWellFormed(code) ? code : ''
}

/** The server's shape rule, so the screen can say why before it sends anything. */
export const codeWellFormed = (code: string): boolean =>
  /^[A-Z][A-Z0-9_]{0,30}[A-Z0-9]$/.test(code)

export const MIN_PROMISE_MINUTES = 1
export const MAX_PROMISE_MINUTES = 24 * 60
export const DEFAULT_PROMISE_MINUTES = 45
